import React, { useState, useCallback } from 'react';
import { Formik, FormikHelpers } from 'formik';

import classNames from '@app/ui/sports-components/utils/classNames';
import { Button, Divider, Flex, Form, Input, Typography } from '@app/ui';
import {
    ArrowLeftOutlined,
    CheckSquareFilled,
    CloseOutlined,
    EnterOutlined,
} from '@ant-design/icons';

import strengthCalculator from '@app/utils/passwordStrengthCalculator';
import { useNavigate } from 'react-router-dom';

export type ResetPasswordFormProps = {
    onSubmit: (
        values: { password: string; confirmedPassword: string },
        props: any,
    ) => Promise<void>;
    message?: { type?: string; text?: string };
};

const initialValues = {
    password: '',
    confirmedPassword: '',
};

const validate = (values: { password: string; confirmedPassword: string }) => {
    const errors: { password?: string; confirmedPassword?: string } = {};

    if (!values.password) {
        errors.password = 'Required';
    }
    if (!values.confirmedPassword) {
        errors.confirmedPassword = 'Required';
    }
    if (values.password !== values.confirmedPassword) {
        errors.confirmedPassword = 'Passwords must match';
    }
    return errors;
};

export const ResetPasswordForm = ({
    onSubmit,
    message,
}: ResetPasswordFormProps) => {
    const navigate = useNavigate();

    const [passwordStrength, setPasswordStrength] = useState(
        strengthCalculator(''),
    );

    const checkPasswordStrength = useCallback(
        (handleChange: any) => (e: any) => {
            const strengthObj = strengthCalculator(e.target.value);
            setPasswordStrength(strengthObj);
            handleChange(e);
        },
        [],
    );

    return (
        <Flex vertical className="-form">
            <Formik
                initialValues={initialValues}
                validate={validate}
                onSubmit={async (
                    values,
                    formikHelpers: FormikHelpers<typeof initialValues>,
                ) => {
                    formikHelpers.setSubmitting(true);
                    await onSubmit(values, formikHelpers);
                    formikHelpers.setSubmitting(false);
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                }) => {
                    const handlePasswordChange =
                        checkPasswordStrength(handleChange);
                    const passwordError = !!(
                        touched.password && errors.password
                    );
                    const confirmedPasswordError = !!(
                        touched.confirmedPassword && errors.confirmedPassword
                    );

                    const messageClasses = classNames(
                        'gds-text--body-sm',
                        '-text-center',
                        '-m-t-2',
                        '-m-b-1',
                        {
                            [`-color-tx-${message?.type}`]: message?.type,
                        },
                    );

                    const ready =
                        passwordStrength.valid &&
                        values.confirmedPassword === values.password;
                    return (
                        <form className="-p-a-3" onSubmit={handleSubmit}>
                            <div className="-m-b-3">
                                <Typography.Paragraph className="-m-b-0">
                                    {passwordStrength.lengthRequirement ? (
                                        <CheckSquareFilled
                                            style={{
                                                color: 'green',
                                                fontSize: 16,
                                            }}
                                        />
                                    ) : (
                                        <CloseOutlined
                                            style={{ color: 'red' }}
                                        />
                                    )}{' '}
                                    8+ characters
                                </Typography.Paragraph>
                                <Typography.Paragraph className="-m-b-0">
                                    {passwordStrength.uppercaseExists ? (
                                        <CheckSquareFilled
                                            style={{
                                                color: 'green',
                                                fontSize: 16,
                                            }}
                                        />
                                    ) : (
                                        <CloseOutlined
                                            style={{ color: 'red' }}
                                        />
                                    )}{' '}
                                    1+ uppercase letter
                                </Typography.Paragraph>
                                <Typography.Paragraph className="-m-b-0">
                                    {passwordStrength.lowercaseExists ? (
                                        <CheckSquareFilled
                                            style={{
                                                color: 'green',
                                                fontSize: 16,
                                            }}
                                        />
                                    ) : (
                                        <CloseOutlined
                                            style={{ color: 'red' }}
                                        />
                                    )}{' '}
                                    1+ lowercase letter
                                </Typography.Paragraph>
                                <Typography.Paragraph className="-m-b-0">
                                    {passwordStrength.numberExists ? (
                                        <CheckSquareFilled
                                            style={{
                                                color: 'green',
                                                fontSize: 16,
                                            }}
                                        />
                                    ) : (
                                        <CloseOutlined
                                            style={{ color: 'red' }}
                                        />
                                    )}{' '}
                                    1+ number
                                </Typography.Paragraph>
                                <Typography.Paragraph className="-m-b-0">
                                    {passwordStrength.specialCharExists ? (
                                        <CheckSquareFilled
                                            style={{
                                                color: 'green',
                                                fontSize: 16,
                                            }}
                                        />
                                    ) : (
                                        <CloseOutlined
                                            style={{ color: 'red' }}
                                        />
                                    )}{' '}
                                    1+ special character
                                </Typography.Paragraph>
                            </div>
                            <Form.Item
                                layout="vertical"
                                className="-m-b-3"
                                label="Password"
                                validateStatus={
                                    passwordError ? 'error' : undefined
                                }
                                help={
                                    passwordError &&
                                    typeof errors.password === 'string'
                                        ? errors.password
                                        : undefined
                                }
                            >
                                <Input.Password
                                    type="password"
                                    name="password"
                                    onChange={handlePasswordChange}
                                    onBlur={handleBlur}
                                    value={values.password}
                                />
                            </Form.Item>
                            <Form.Item
                                layout="vertical"
                                label="Re-enter Password"
                                validateStatus={
                                    confirmedPasswordError ? 'error' : undefined
                                }
                                help={
                                    confirmedPasswordError &&
                                    typeof errors.confirmedPassword === 'string'
                                        ? errors.confirmedPassword
                                        : undefined
                                }
                            >
                                <Input.Password
                                    type="password"
                                    name="confirmedPassword"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.confirmedPassword}
                                />
                            </Form.Item>
                            {message && message.type && (
                                <p className={messageClasses}>{message.text}</p>
                            )}
                            <Form.Item className="-m-t-3">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<EnterOutlined />}
                                    iconPosition="end"
                                    loading={isSubmitting}
                                    disabled={!ready || isSubmitting}
                                >
                                    Set Password
                                </Button>
                            </Form.Item>

                            <Divider />

                            <Button
                                onClick={() => navigate('/login')}
                                type="link"
                                icon={
                                    <ArrowLeftOutlined
                                        style={{
                                            color: 'rgba(0, 159, 223, 1)',
                                        }}
                                    />
                                }
                                iconPosition="start"
                            >
                                Back to Sign In
                            </Button>
                        </form>
                    );
                }}
            </Formik>
        </Flex>
    );
};

export default ResetPasswordForm;
